import { Controller } from "@hotwired/stimulus"
import { initCropper, destroyCropper, getCroppedBlob, rotateImage, lockBodyScroll, unlockBodyScroll } from "../helpers/cropper_helper"

export default class extends Controller {
  static targets = ["modal", "cropImage", "spinner"]
  static values = { url: String, patchUrl: String }

  open(event) {
    event.stopPropagation()
    event.preventDefault()

    this.modal = this.modalTarget
    this.cropImage = this.cropImageTarget
    this.spinner = this.hasSpinnerTarget ? this.spinnerTarget : null

    document.body.appendChild(this.modal)
    this.modal.classList.remove("hidden")
    lockBodyScroll()
    this.bindModalActions()

    this.cropImage.onload = () => this.initializeCropper()

    const src = this.urlValue
    if (this.cropImage.src === src && this.cropImage.complete) {
      this.initializeCropper()
    } else {
      this.cropImage.src = src
    }
  }

  initializeCropper() {
    this.cropper = destroyCropper(this.cropper)
    this.cropper = initCropper(this.cropImage)
  }

  close() {
    this.cropper = destroyCropper(this.cropper)
    this.unbindModalActions()
    this.modal.classList.add("hidden")
    this.element.appendChild(this.modal)
    unlockBodyScroll()
  }

  zoomIn() { if (this.cropper) this.cropper.zoom(0.1) }
  zoomOut() { if (this.cropper) this.cropper.zoom(-0.1) }

  rotateLeft() { this.applyRotation(-90) }
  rotateRight() { this.applyRotation(90) }

  applyRotation(degrees) {
    if (!this.cropper) return
    this.cropper = destroyCropper(this.cropper)
    rotateImage(this.cropImage, degrees, () => this.initializeCropper())
  }

  async save() {
    if (!this.cropper) return

    const blob = await getCroppedBlob(this.cropper)
    if (!blob) return

    this.toggleSpinner(true)

    const form = new FormData()
    form.append("photo", new File([blob], "edited.jpg", { type: "image/jpeg" }))

    const token = document.querySelector("meta[name='csrf-token']")?.content

    try {
      await fetch(this.patchUrlValue, {
        method: "PATCH",
        headers: { "X-CSRF-Token": token, Accept: "text/html" },
        redirect: "manual",
        body: form
      })

      this.close()
      window.Turbo.visit(window.location.href, { action: "replace" })
    } catch {
      this.toggleSpinner(false)
    }
  }

  toggleSpinner(show) {
    if (!this.spinner) return
    this.spinner.classList.toggle("hidden", !show)
  }

  disconnect() {
    this.cropper = destroyCropper(this.cropper)
    if (this.modal?.parentElement === document.body) {
      this.unbindModalActions()
      this.element.appendChild(this.modal)
    }
    unlockBodyScroll()
  }

  // private — bind actions on modal buttons when modal is detached from controller
  bindModalActions() {
    this.handlers = {
      close: () => this.close(),
      rotateLeft: () => this.rotateLeft(),
      rotateRight: () => this.rotateRight(),
      zoomIn: () => this.zoomIn(),
      zoomOut: () => this.zoomOut(),
      save: () => this.save()
    }

    this.modal.querySelectorAll("[data-modal-action]").forEach(btn => {
      const action = btn.dataset.modalAction
      if (this.handlers[action]) btn.addEventListener("click", this.handlers[action])
    })
  }

  unbindModalActions() {
    if (!this.handlers) return

    this.modal.querySelectorAll("[data-modal-action]").forEach(btn => {
      const action = btn.dataset.modalAction
      if (this.handlers[action]) btn.removeEventListener("click", this.handlers[action])
    })

    this.handlers = null
  }
}
